import { createContext, useContext, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { userRepository, profileActions, trackingService } from "../../../di/di";
import Toast from "../../../components/popups/toast";
import { getUser as getProfileUser } from "../../profile/state/profile_slice";
import { getUser as getUserSliceUser } from "../../../state/slice/user_slice";

const ProfileUpdateContext = createContext();

export const ProfileUpdateContextProvider = ({ children }) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.profile.user);
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [originalEmail, setOriginalEmail] = useState(user?.email || "");
  const [otp, setOtp] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [nameErrorMessage, setNameErrorMessage] = useState("");
  const [emailErrorMessage, setEmailErrorMessage] = useState("");
  const [otpErrorMessage, setOtpErrorMessage] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);

  // Update form fields when user data changes
  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setEmail(user.email || "");
      setOriginalEmail(user.email || "");
    }
  }, [user]);

  // Check if email has been changed
  const isEmailChanged = email !== originalEmail;

  // Timer effect
  useEffect(() => {
    let interval;
    if (otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [otpTimer]);

  // Disable update button if email is changed but OTP is not verified
  const isUpdateDisabled = isEmailChanged && !isOtpVerified;

  const isFormDisabled = nameErrorMessage || emailErrorMessage || otpErrorMessage || isProcessing || isUpdateDisabled;

  const handleNameChange = (e) => {
    setNameErrorMessage("");
    const value = e.target.value;
    setName(value);

    if (!value) {
      setNameErrorMessage("Name is required");
    }
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmailErrorMessage("");
    setEmail(value);
    
    // Reset OTP states when email changes
    if (value !== originalEmail) {
      setIsOtpSent(false);
      setIsOtpVerified(false);
      setOtp("");
      setOtpErrorMessage("");
      setOtpTimer(0);
    }

    if (!value.includes("@")) {
      setEmailErrorMessage("Invalid email address");
    }
  };

  const handleOtpChange = (e) => {
    const value = e.target.value;
    setOtpErrorMessage("");
    setOtp(value);

    if (!value) {
      setOtpErrorMessage("OTP is required");
    }
  };

  const handleSendOtp = async () => {
    if (!email || emailErrorMessage) {
      setEmailErrorMessage("Please enter a valid email first");
      return;
    }

    setIsProcessing(true);
    try {
      const result = await userRepository.sendOTP(email);
      if (result.success) {
        setIsOtpSent(true);
        setIsOtpVerified(false);
        setErrorMessage("");
        setOtpTimer(60); // Start 60 second timer
      } else {
        setErrorMessage(result.message || "Failed to send OTP");
      }
    } catch (error) {
      setErrorMessage("An error occurred while sending OTP");
      console.error("Error sending OTP:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp) {
      setOtpErrorMessage("Please enter OTP");
      return;
    }

    setIsProcessing(true);
    try {
      const result = await userRepository.validateOTP(email, otp);
      if (result.success) {
        setOtpErrorMessage("");
        setIsOtpVerified(true);
        setIsOtpSent(false);
        setOtpTimer(0);
        setErrorMessage("");
      } else {
        setOtpErrorMessage(result.message || "Invalid OTP");
        setIsOtpVerified(false);
      }
    } catch (error) {
      setOtpErrorMessage("An error occurred while verifying OTP");
      console.error("Error verifying OTP:", error);
      setIsOtpVerified(false);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setIsProcessing(true);
    setErrorMessage("");
    trackingService.trackEvent("edit_profile_initiated", { userId: user?.id, timestamp: new Date().toISOString() });
    try {
      const result = await userRepository.updateProfile({name, email});
      if (result.success) {
        // Reset form fields to their original state
        setName(user?.name || "");
        setEmail(user?.email || "");
        setOriginalEmail(user?.email || "");
        
        // Reset OTP states
        setOtp("");
        setIsOtpSent(false);
        setIsOtpVerified(false);
        setOtpErrorMessage("");
        setOtpTimer(0);
        
        // Show success toast
        Toast.success("Profile updated successfully");
        
        // Close the modal using the correct action creator
        dispatch(profileActions.setEditMode(false));

        // Fetch the latest user data to update all slices
        dispatch(getProfileUser());
        dispatch(getUserSliceUser());
        trackingService.trackEvent("edit_profile_success", { userId: user?.id, timestamp: new Date().toISOString() });
      } else {
        setErrorMessage(result.message || "Failed to update profile");
        trackingService.trackEvent("edit_profile_failed", { userId: user?.id, failure_reason: result.message, timestamp: new Date().toISOString() });
      }
    } catch (error) {
      setErrorMessage(error.message || "An error occurred while updating profile");
      trackingService.trackEvent("edit_profile_failed", { userId: user?.id, failure_reason: error.message, timestamp: new Date().toISOString() });
      console.error("Error updating profile:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <ProfileUpdateContext.Provider
      value={{
        name,
        email,
        otp,
        errorMessage,
        nameErrorMessage,
        emailErrorMessage,
        otpErrorMessage,
        isProcessing,
        isFormDisabled,
        isOtpSent,
        isOtpVerified,
        isEmailChanged,
        otpTimer,
        handleNameChange,
        handleEmailChange,
        handleOtpChange,
        handleSendOtp,
        handleVerifyOtp,
        handleProfileUpdate,
      }}
    >
      {children}
    </ProfileUpdateContext.Provider>
  );
};

export const useProfileUpdateContext = () => {
  return useContext(ProfileUpdateContext);
};
